/**
 * The five bay states and — the part that matters — the second, non-colour cue
 * each one carries, so the map survives deuteranopia and a bad basement monitor.
 * IMPLEMENTATION-PLAN.md §4; figure `docs/figures/web/00-…`.
 *
 * `unknown` must never look like `free`. During commissioning most of the map is
 * unknown, and a tool that paints unknown as free tells the installation team the
 * job is finished when it has not started. That is a correctness requirement.
 */

export const BAY_STATES = ['free', 'occupied', 'fault', 'unavailable', 'unknown'] as const;

export type BayState = (typeof BAY_STATES)[number];

/**
 * The two overlay cues.
 *
 * `diagonal` is an SVG pattern fill; `cross` is drawn as two corner-to-corner
 * lines rather than a pattern, because at the map's 22 x 14 unit bay a
 * repeating cross-hatch is a grey smudge and a single X still reads.
 */
export type BayOverlay = 'diagonal' | 'cross';

/**
 * The pattern id, namespaced by the id of the <svg> that defines it.
 *
 * Duplicate ids across several inline SVGs are a real trap: `url(#id)`
 * resolves against the whole document, so five legend swatches all declaring
 * `pv-hatch-diagonal` would every one of them paint the first definition —
 * which is how the fault stripes came to be invisible the first time.
 */
export function hatchPatternId(scope: string): string {
  return `pv-hatch-diagonal-${scope}`;
}

/** Diagonal stripe geometry, in the units of whatever paints it. */
export const HATCH_STRIPE_WIDTH = 2.2;
export const HATCH_STRIPE_PITCH = 5;

export interface BayStateStyle {
  readonly state: BayState;
  /** Label as it appears in the legend and in `aria-label`. */
  readonly label: string;
  /** The second, non-colour cue — the thing that survives grayscale. */
  readonly secondCue: string;
  readonly fill: string;
  readonly stroke: string;
  readonly strokeWidth: number;
  /** SVG `stroke-dasharray`, or `undefined` for a solid outline. */
  readonly strokeDasharray?: string;
  /** The non-colour overlay painted over the fill, or `undefined`. */
  readonly overlay?: BayOverlay;
  /** Colour of that overlay. */
  readonly overlayColor?: string;
  /** Colour of the two-digit bay number drawn inside the object. */
  readonly labelColor: string;
  /**
   * The state's name **as text**, at WCAG AA on white.
   *
   * `stroke` is an outline colour and is chosen for the map; several of the
   * outlines land between 2.4:1 and 2.9:1, which is fine for a 1.4 px line
   * and fails for an 11 px word. See DEFECT-5.
   */
  readonly textColor: string;
}

export const BAY_STATE_STYLES: Readonly<Record<BayState, BayStateStyle>> = {
  free: {
    state: 'free',
    label: 'Free',
    secondCue: 'white fill, 1.8 px green outline',
    fill: '#FFFFFF',
    stroke: 'var(--pv-free)',
    strokeWidth: 1.8,
    labelColor: 'var(--pv-ink)',
    textColor: 'var(--pv-free)',
  },
  occupied: {
    state: 'occupied',
    label: 'Occupied',
    secondCue: 'solid charcoal fill, white code',
    fill: 'var(--pv-occupied)',
    stroke: 'var(--pv-occupied)',
    strokeWidth: 1,
    labelColor: '#FFFFFF',
    textColor: 'var(--pv-occupied)',
  },
  fault: {
    state: 'fault',
    label: 'Fault',
    secondCue: 'amber, diagonal hatch',
    // A pale ground, so the amber stripes are the thing that reads. Filling
    // solid amber and hatching in amber hides the cue the state depends on.
    fill: 'var(--pv-color-bay-fault-fill)',
    stroke: 'var(--pv-accent)',
    strokeWidth: 1.4,
    overlay: 'diagonal',
    overlayColor: '#B8730F',
    labelColor: 'var(--pv-ink)',
    textColor: 'var(--pv-accent-text)',
  },
  unavailable: {
    state: 'unavailable',
    label: 'Maintenance hold',
    secondCue: 'grey, cross hatch',
    fill: '#EAE7E7',
    stroke: 'var(--pv-color-bay-unavailable)',
    strokeWidth: 1.4,
    overlay: 'cross',
    overlayColor: 'var(--pv-color-bay-unavailable)',
    labelColor: 'var(--pv-ink)',
    textColor: 'var(--pv-muted)',
  },
  unknown: {
    state: 'unknown',
    label: 'Unknown',
    // The dash is the whole point: it is what stops unknown reading as free.
    secondCue: 'white, dashed grey outline',
    fill: '#FFFFFF',
    stroke: 'var(--pv-color-bay-unknown)',
    strokeWidth: 1.4,
    strokeDasharray: '3 2.5',
    labelColor: 'var(--pv-muted)',
    textColor: 'var(--pv-muted)',
  },
};

/** Opacity applied to a bay excluded by the current filter. Never hidden — §9.2. */
export const EXCLUDED_BAY_OPACITY = 0.25;

/** Opacity applied to every live figure once data is beyond two poll intervals — §9.1. */
export const STALE_DIM_OPACITY = 0.6;

/**
 * Bay *kinds* — what a bay is, as opposed to the five states, which are what it is doing now.
 *
 * A valet bay and a disabled bay are each free or occupied or anything else, so a kind is never a
 * sixth state and never touches the fill: it is a round badge on the bay's corner, white on a colour
 * of its own, with a letter or a figure so it survives grayscale. UDC's numbering sheet (ED.RV.1)
 * marks 88 bays "VP (Valet Parking)" and 13 "DISABLED P"; spec/024-plot-bay-master-data.
 */
export const BAY_KINDS = ['valet', 'disabled'] as const;

export type BayKind = (typeof BAY_KINDS)[number];

export interface BayKindStyle {
  readonly kind: BayKind;
  /** The name in the legend, the filter and the accessible label. */
  readonly label: string;
  /** Badge colour; white glyph on it is above 6:1. */
  readonly fill: string;
}

export const BAY_KIND_STYLES: Readonly<Record<BayKind, BayKindStyle>> = {
  valet: { kind: 'valet', label: 'Valet', fill: '#5B3FA0' },
  disabled: { kind: 'disabled', label: 'Disabled', fill: '#1D5FBF' },
};
